import { defineComponent } from "vue";
import { mapState } from "pinia";
import { Container } from "aurelia-dependency-injection";
import { TaskQueue } from "aurelia-framework";
import useVuelidate from "@vuelidate/core";
import { helpers, requiredIf } from "@vuelidate/validators";

import config from "../../../../../common/config";
import { useHospiceConvertToPending } from "../../store/useHospiceConvertToPending";
import { VendorsService } from "../../../../../services/vendor-service";
import { IUpdatedPlacesAddress } from "../../../../../resources-vue/vue-interfaces/i-places-autocomplete";
import { formatZipCode } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { LookupService } from "../../../../../services/lookup-service";
import { IGetVendorByIdQuery } from "../../../../../resources-vue/vue-interfaces/i-vendor";
import { Address } from "../../../../../resources-vue/vue-models/address";
import { getHospiceRedirectionUrl } from "../../../../../common/get-hospice-redirection-url";

import CountryTypeahead from "../../../../../resources-vue/vue-custom-elements/CountryTypeahead/CountryTypeahead.vue";
import StateTypeahead from "../../../../../resources-vue/vue-custom-elements/StateTypeahead/StateTypeahead.vue";
import PlacesAutocomplete from "../../../../../resources-vue/vue-custom-elements/PlacesAutocomplete/PlacesAutocomplete.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";

export default defineComponent({
    components: {
        CountryTypeahead,
        StateTypeahead,
        PlacesAutocomplete,
        TypeaheadInput
    },

    props: {
        isFacility: {
            type: Boolean,
            default: true
        },
        addressType: {
            type: String
        },
        providerId: {
            type: String,
            default: ""
        },
        address: {
            type: Object
        },
        facilityId: {
            type: String
        },

        isPrimaryAddress: {
            type: Boolean,
            default: false
        },
        isAddressValidationInProgress: {
            type: Boolean,
            default: false
        },

        isRequired: {
            type: Boolean,
            default: false
        },

        showCode: {
            type: Boolean,
            default: true
        }
    },

    data(vm) {
        return {
            _vendorsService: null,
            _lookupService: null,
            _autoCompleteAddress: null,
            _tq: null,
            selectedFacility: null,
            selectedCountry: null,
            selectedState: null,
            unitedStatesCountryCode: "",
            autoCompleteCountry: "",
            v$: useVuelidate(),
            formatZipCode
        };
    },

    validations() {
        return {
            address: {
                addressLine1: {
                    required: helpers.withMessage(
                        this.addressType,
                        requiredIf(() => this.isRequired)
                    )
                },
                country: {
                    required: helpers.withMessage(
                        this.addressType,
                        requiredIf(() => this.isRequired)
                    )
                },
                zipCode: {
                    required: helpers.withMessage(
                        this.addressType,
                        requiredIf(() => this.isRequired)
                    )
                },

                state: {
                    required: helpers.withMessage(
                        this.addressType,
                        requiredIf(() => this.isRequired)
                    )
                },
                city: {
                    required: helpers.withMessage(
                        this.addressType,
                        requiredIf(() => this.isRequired)
                    )
                }
            }
        };
    },

    emits: ["update:facilityId", "update:address"],

    created() {
        this._vendorsService = Container.instance.get(VendorsService);
        this._lookupService = Container.instance.get(LookupService);
        this._tq = Container.instance.get(TaskQueue);
    },

    async beforeMount() {
        if (!this.facilityId) {
            this.selectedFacility = null;
        } else {
            const payload: IGetVendorByIdQuery = {
                application: Number(this.application),
                accountId: this.accountId as string,
                id: this.facilityId
            };
            const selectedVendor = await this._vendorsService.getVendorById(payload);
            if (!!selectedVendor) {
                this.selectedFacility = {
                    name: selectedVendor.name,
                    value: selectedVendor.id
                };
            }
        }
        this.unitedStatesCountryCode = await this._lookupService.getUnitedStatesCode();
        if (!this.address.country) {
            this.address.country = this.unitedStatesCountryCode;
        }
        if (this.isPrimaryAddress) {
            if (!this.address.latitude || !this.address.longitude) {
                this.address.isValidated = false;
            }
        }
    },

    computed: {
        ...mapState(useHospiceConvertToPending, {
            accountId: "accountId",
            patientDetails: "patientDetails",
            application: "application",
            countryList: "countryList"
        }),
        isUnitedStates() {
            return this.selectedCountry?.name.toLowerCase() === "united states of america";
        }
    },

    watch: {
        selectedFacility: {
            async handler(newValue, oldValue) {
                if (newValue !== oldValue) {
                    if (!!this.selectedFacility && this.selectedFacility.value !== this.facilityId) {
                        this.$emit("update:facilityId", this.selectedFacility.value);
                        const payload: IGetVendorByIdQuery = {
                            application: Number(this.application),
                            accountId: this.accountId as string,
                            id: this.facilityId
                        };
                        const selectedVendor = await this._vendorsService.getVendorById(payload);
                        if (!!selectedVendor) {
                            this.address.addressLine1 = selectedVendor.address.addressLine1;
                            this.address.addressLine2 = selectedVendor.address.addressLine2;
                            this.address.city = selectedVendor.address.city;
                            this.address.state = selectedVendor.address.state;
                            this.address.zipCode = selectedVendor.address.zipCode;
                            this.address.county = selectedVendor.address.county as string;
                            this.address.country = selectedVendor.address.country;
                            this.address.isValidated = selectedVendor.address.isValidated;
                            this.address.latitude = selectedVendor.address.latitude;
                            this.address.longitude = selectedVendor.address.longitude;
                        }
                    } else if (!this.selectedFacility) {
                        this.$emit("update:facilityId", null);
                        this.$emit("update:address", new Address());
                        if (this.isUnitedStates) {
                            this.address.initValidations();
                        } else {
                            this.address.initInternationalValidation();
                        }
                    }
                }
            },
            deep: true
        },

        autoCompleteCountry() {
            const country = this.countryList?.find((data: any) => data.twoLetterCode === this.autoCompleteCountry);
            let temp = { ...this.address };
            temp.country = country?.value;
            this.$emit("update:address", temp);
        },

        selectedState() {
            const temp = { ...this.address };
            temp.state = this.selectedState.value;
            this.$emit("update:address", temp);
        }
    },

    methods: {
        handleDirectionToFacility() {
            window.open(
                `${getHospiceRedirectionUrl(String(this.accountId))}/create/vendor?accountId=${this.accountId}`
            );
        },

        async getVendorList(filter: string, limit: number) {
            const payload = {
                accountId: this.accountId,
                name: filter,
                providerIds: [this.patientDetails.providerId],
                application: Number(this.application)
            };
            const facilityList = await this._vendorsService.getVendorByTerm(payload);
            return facilityList.map((facility: any) => ({
                name: facility.name,
                value: facility.id
            }));
        },
        updateFacility(value: any) {
            this.selectedFacility = value;
        },
        updateSelectedCountry(value: any) {
            this.selectedCountry = value;
            let temp = { ...this.address };
            if (!!this.selectedCountry) {
                temp.country = this.selectedCountry.value;
            } else {
                temp.country = "";
            }
            this.$emit("update:address", temp);
        },
        placesChangedCallBack(params: IUpdatedPlacesAddress) {
            if (!params) {
                return;
            }
            Object.assign(this._autoCompleteAddress, params);
            this.address.addressLine1 = params.addressLine1;
            this.address.city = params.city;
            this.address.county = params.county;
            this.address.state = params.state;
            this.address.zipCode = params.zipCode;
            this.address.latitude = params.latitude;
            this.address.longitude = params.longitude;
            this.address.isValidated = true;
            this.address.isBypassed = false;
        },
        handleZipcodeChange(e: any) {
            this.address.zipCode = e.target.value;
        }
    }
});
